import { Head, Link, router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import {
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    DocumentTextIcon,
    ArrowDownTrayIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    BeakerIcon,
} from '@heroicons/react/24/outline';

export default function Index({ reports, filters, flash }) {
    const { auth } = usePage().props;
    const userPerms = auth?.user?.permissions || [];
    const isAdmin = auth?.user?.is_admin;
    const can = (perm) => isAdmin || userPerms.includes(perm);

    const [search, setSearch] = useState(filters?.search || '');
    const [status, setStatus] = useState(filters?.status || '');
    const [dateFrom, setDateFrom] = useState(filters?.date_from || '');
    const [dateTo, setDateTo] = useState(filters?.date_to || '');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const applyFilters = (e) => {
        e?.preventDefault();
        router.get('/daily-reports', {
            search,
            status,
            date_from: dateFrom,
            date_to: dateTo,
        }, { preserveState: true });
    };

    const clearFilters = () => {
        setSearch('');
        setStatus('');
        setDateFrom('');
        setDateTo('');
        router.get('/daily-reports');
    };

    const statusBadge = (s) => {
        const map = { draft: 'warning', final: 'success' };
        return <Badge variant={map[s] || 'neutral'}>{s || 'draft'}</Badge>;
    };

    return (
        <AppLayout>
            <Head title="Daily Reports" />
            <PageHeader
                title="Daily Reports"
                subtitle="Track daily field operations and water unit readings"
                actions={can('daily-reports.create') ? (
                    <Button href="/daily-reports/create" size="sm">
                        <PlusIcon className="w-4 h-4" /> New Report
                    </Button>
                ) : null}
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            {/* Filters */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3 items-end">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search reports..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                            />
                        </div>
                        <FormSelect value={status} onChange={e => setStatus(e.target.value)} className="w-full sm:w-36">
                            <option value="">All Status</option>
                            <option value="draft">Draft</option>
                            <option value="final">Final</option>
                        </FormSelect>
                        <FormInput
                            label="From"
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            className="w-full sm:w-44"
                        />
                        <FormInput
                            label="To"
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            className="w-full sm:w-44"
                        />
                        <div className="flex gap-2">
                            <Button type="submit" size="sm">
                                <FunnelIcon className="w-4 h-4" /> Apply
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                                Clear
                            </Button>
                        </div>
                    </form>
                </CardBody>
            </Card>

            {/* Reports Table */}
            <Card>
                <Table minWidth="800px">
                    <thead>
                        <tr>
                            <Th>SL</Th>
                            <Th>Date</Th>
                            <Th>Day</Th>
                            <Th>Foreman</Th>
                            <Th>Status</Th>
                            <Th>Water Units</Th>
                            <Th>Images</Th>
                            <Th>Created By</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {reports?.map?.((r, idx) => {
                            const canEdit = r.status === 'draft' || can('daily-reports.edit');
                            return (
                            <Tr key={r.id} className={canEdit ? 'cursor-pointer' : ''} onClick={() => { if (canEdit) router.get(`/daily-reports/${r.id}/edit`); }}>
                                <Td className="font-semibold text-slate-600 text-xs">{reports.length - idx}</Td>
                                <Td className="font-medium text-slate-800 whitespace-nowrap">
                                    {r.date ? r.date.split('T')[0].split('-').reverse().join('/') : '—'}
                                </Td>
                                <Td className="whitespace-nowrap text-sm text-slate-600">
                                    {r.day || '—'}
                                </Td>
                                <Td className="whitespace-nowrap">{r.foreman_name || '—'}</Td>
                                <Td>{statusBadge(r.status)}</Td>
                                <Td>
                                    <Badge variant="info">{r.water_units?.length || 0}</Badge>
                                </Td>
                                <Td>
                                    <Badge variant="neutral">{r.images?.length || 0}</Badge>
                                </Td>
                                <Td className="text-sm text-slate-500 whitespace-nowrap">
                                    {r.created_by_user?.name || r.created_by?.name || '—'}
                                </Td>
                                <Td>
                                    <div className="flex flex-wrap items-center gap-1" onClick={e => e.stopPropagation()}>
                                        {canEdit && (
                                            <Link href={`/daily-reports/${r.id}/edit`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition">
                                                <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
                                            </Link>
                                        )}
                                        {can('daily-reports.reports') && (
                                            <>
                                                <a href={`/daily-reports/${r.id}/report-pdf`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition">
                                                    <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Report
                                                </a>
                                                <a href={`/daily-reports/${r.id}/water-unit-pdf`} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition">
                                                    <BeakerIcon className="w-3.5 h-3.5" /> Water
                                                </a>
                                            </>
                                        )}
                                        {can('daily-reports.delete') && (
                                            <button onClick={() => setDeleteConfirm(r.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition">
                                                <TrashIcon className="w-3.5 h-3.5" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </Td>
                            </Tr>
                        );})}
                    </tbody>
                </Table>
                {(!reports || reports.length === 0) && (
                    <EmptyState
                        icon={DocumentTextIcon}
                        title="No daily reports"
                        description="Create your first daily report to track field operations."
                        action={
                            <Button href="/daily-reports/create" size="sm">
                                <PlusIcon className="w-4 h-4" /> New Report
                            </Button>
                        }
                    />
                )}
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete Daily Report"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete this daily report? This will also remove all associated images and water unit data.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            router.delete(`/daily-reports/${deleteConfirm}`);
                            setDeleteConfirm(null);
                        }}
                    >
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
